import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Form, Input, message, Spin, Modal } from 'antd';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import QRCode from "react-qr-code";
import { useGlobalProps } from '@hooks/useGlobalProps';
import { getNewUser } from '@api/user';
import { STATUS_CODE } from '@constants/api';
import AlertModal from '@components/shared/modals/AlertModal';
import { FaCopy, FaQrcode, FaEye } from 'react-icons/fa';

const Container = styled.div`
    padding: 20px;
    max-width: 600px;
    margin: 0 auto;
`;

const FormContainer = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
    text-align: center;
    margin-bottom: 24px;
    color: #333;
`;

const ResultContainer = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-top: 20px;
`;

const QRContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 20px 0;
`;

const URLContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    background: #f5f5f5;
    padding: 12px;
    border-radius: 4px;
    margin: 20px 0;
`;

const URLText = styled.span`
    flex: 1;
    word-break: break-all;
    font-size: 14px;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 10px;
`;

const StyledButton = styled.button`
    font-size: 1rem;
    font-family: inherit;
    border: none;
    border-radius: 8px;
    padding: 0.75rem 1.5rem;
    box-shadow: 0 0px 1px hsla(0, 0%, 0%, 0.2), 0 1px 2px hsla(0, 0%, 0%, 0.2);
    background-color: ${props => props.primary ? 'rgba(167, 23, 26, 1)' : 'white'};
    color: ${props => props.primary ? 'white' : '#3992ff'};
    line-height: 1.5;
    margin: 0;
    cursor: pointer;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: ${props => props.fullWidth ? '100%' : 'auto'};

    &:hover {
        box-shadow: 0 0px 1px hsla(0, 0%, 0%, 0.6), 0 1px 2px hsla(0, 0%, 0%, 0.2);
        background-color: ${props => props.primary ? 'rgba(167, 23, 26, 0.9)' : '#f5f5f5'};
    }

    &:active {
        box-shadow: 0 0px 1px hsla(0, 0%, 0%, 0.4);
        transform: translateY(1px);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    svg {
        font-size: 1rem;
    }
`;

export default function NewUserPage() {
    const { t } = useTranslation();
    const { user } = useGlobalProps();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [userURL, setUserURL] = useState('');
    const [showQRModal, setShowQRModal] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();

    const tCommon = t('common', { returnObjects: true });
    const tError = t('error', { returnObjects: true });

    const validatePhone = (_, value) => {
        if (!value) {
            return Promise.resolve();
        }
        // Basic phone validation - adjust pattern as needed
        const phoneRegex = /^[0-9+\-() ]+$/;
        if (!phoneRegex.test(value) || value.length < 8 || value.length > 20) {
            return Promise.reject(new Error(tCommon['common@invalid-phone']));
        }
        return Promise.resolve();
    };

    const validateEmail = (_, value) => {
        if (!value) {
            return Promise.resolve();
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            return Promise.reject(new Error(tCommon['common@invalid-email']));
        }
        return Promise.resolve();
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const params = {
                ukey: user.ukey,
                tel: values.phone,
                email: values.email || ''
            };

            const response = await getNewUser(params);

            if (response.rtn === STATUS_CODE.SUCCESS) {
                setUserURL(response.userURL);
                messageApi.success(tCommon['common@success']);
                form.resetFields(['phone', 'email']);
            } else {
                messageApi.error(response.msg || tCommon['common@failed']);
            }
        } catch (error) {
            messageApi.error(tError['error@network-error'] || 'Network error');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyURL = () => {
        messageApi.success(tCommon['common@copy'] + ' ' + tCommon['common@success']);
    };

    return (
        <Container>
            {contextHolder}

            <FormContainer>
                <Title>{tCommon['common@new-user']}</Title>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        partner: user?.acc || ''
                    }}
                >
                    <Form.Item
                        label={tCommon['common@partner']}
                        name="partner"
                    >
                        <Input disabled />
                    </Form.Item>

                    <Form.Item
                        label={tCommon['common@phone-number']}
                        name="phone"
                        rules={[
                            { required: true, message: tCommon['common@required-field'] },
                            { validator: validatePhone }
                        ]}
                    >
                        <Input placeholder={tCommon['common@phone-number']} />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span>
                                {tCommon['common@email']}
                                <span style={{ color: '#999', fontSize: '12px' }}> ({tCommon['common@optional']})</span>
                            </span>
                        }
                        name="email"
                        rules={[
                            { validator: validateEmail }
                        ]}
                    >
                        <Input placeholder={tCommon['common@email']} />
                    </Form.Item>

                    <Form.Item>
                        <StyledButton
                            type="submit"
                            disabled={loading}
                            primary
                            fullWidth
                        >
                            {loading ? <Spin size="small" /> : tCommon['common@generate-qr']}
                        </StyledButton>
                    </Form.Item>
                </Form>
            </FormContainer>

            {userURL && (
                <ResultContainer>
                    <QRContainer>
                        <div style={{ background: 'white', padding: '16px', borderRadius: '8px' }}>
                            <QRCode
                                size={200}
                                value={userURL}
                                viewBox={`0 0 256 256`}
                            />
                        </div>
                    </QRContainer>

                    <URLContainer>
                        <URLText>{userURL}</URLText>
                    </URLContainer>

                    <ButtonGroup>
                        <CopyToClipboard text={userURL} onCopy={handleCopyURL}>
                            <StyledButton primary>
                                <FaCopy />
                                {tCommon['common@copy']}
                            </StyledButton>
                        </CopyToClipboard>

                        <StyledButton onClick={() => setShowQRModal(true)}>
                            <FaEye />
                            {tCommon['common@view-qr']}
                        </StyledButton>
                    </ButtonGroup>
                </ResultContainer>
            )}

            <Modal
                title={tCommon['common@view-qr']}
                open={showQRModal}
                onCancel={() => setShowQRModal(false)}
                footer={[
                    <StyledButton key="close" onClick={() => setShowQRModal(false)}>
                        {tCommon['common@close']}
                    </StyledButton>,
                    <CopyToClipboard key="copy" text={userURL} onCopy={handleCopyURL}>
                        <StyledButton primary>
                            <FaCopy />
                            {tCommon['common@share-link']}
                        </StyledButton>
                    </CopyToClipboard>
                ]}
                centered
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <QRCode
                        size={256}
                        value={userURL}
                        viewBox={`0 0 256 256`}
                    />
                    <div style={{ marginTop: '20px', wordBreak: 'break-all', fontSize: '12px' }}>
                        {userURL}
                    </div>
                </div>
            </Modal>
        </Container>
    );
}